import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import XLSX from 'xlsx-js-style';
import html2pdf from 'html2pdf.js'; 
import { FaShareAlt, FaPrint, FaArrowLeft, FaFileExcel } from 'react-icons/fa';

const safeNum = (num) => {
    const val = parseFloat(num || 0);
    return parseFloat(val.toFixed(2));
};

const formatCurrency = (num) => {
  if (typeof num !== 'number') { num = parseFloat(num || 0); }
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const formatWeight = (weight) => {
    if (!weight) return '-';
    const val = parseFloat(weight);
    if (isNaN(val)) return '-';
    return Number.isInteger(val) ? `${val.toFixed(1)} KG` : `${val} KG`;
};

const formatMonthYear = (dateString) => { 
    if (!dateString) return "";
    const [year, month] = dateString.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleString('en-US', { month: 'long', year: 'numeric' }).toUpperCase(); 
}

function numberToWordsBDT(num) {
    const a = ['','One ','Two ','Three ','Four ', 'Five ','Six ','Seven ','Eight ','Nine ','Ten ','Eleven ','Twelve ','Thirteen ','Fourteen ','Fifteen ','Sixteen ','Seventeen ','Eighteen ','Nineteen '];
    const b = ['', '', 'Twenty','Thirty','Forty','Fifty', 'Sixty','Seventy','Eighty','Ninety'];
    const inWordsBDTInternal = (n) => {
        let str = '';
        if (n > 9999999) { str += inWordsBDTInternal(Math.floor(n / 10000000)); str += 'Crore '; n %= 10000000; }
        if (n > 99999) { str += inWordsBDTInternal(Math.floor(n / 100000)); str += 'Lakh '; n %= 100000; }
        if (n > 999) { str += inWordsBDTInternal(Math.floor(n / 1000)); str += 'Thousand '; n %= 1000; }
        if (n > 99) { str += inWordsBDTInternal(Math.floor(n / 100)); str += 'Hundred '; n %= 100; }
        if (n > 19) { str += b[Math.floor(n / 10)] + ' ' + a[n % 10]; } else { str += a[n]; }
        return str; 
    };
    const taka = Math.round(parseFloat(num || 0));
    if (taka === 0) return 'BDT ZERO TAKA ONLY';
    let takaWords = inWordsBDTInternal(taka).trim(); 
    let result = 'BDT ' + takaWords + ' Taka Only'; 
    return result.replace(/\s+/g, ' ').toUpperCase(); 
}

function numberToWordsUSD(num) {
    const a = ['','One ','Two ','Three ','Four ', 'Five ','Six ','Seven ','Eight ','Nine ','Ten ','Eleven ','Twelve ','Thirteen ','Fourteen ','Fifteen ','Sixteen ','Seventeen ','Eighteen ','Nineteen '];
    const b = ['', '', 'Twenty','Thirty','Forty','Fifty', 'Sixty','Seventy','Eighty','Ninety'];
    const inWordsInternal = (n) => {
        let str = '';
        if (n > 999999999) { str += inWordsInternal(Math.floor(n / 1000000000)); str += 'Billion '; n %= 1000000000; }
        if (n > 999999) { str += inWordsInternal(Math.floor(n / 1000000)); str += 'Million '; n %= 1000000; }
        if (n > 999) { str += inWordsInternal(Math.floor(n / 1000)); str += 'Thousand '; n %= 1000; }
        if (n > 99) { str += inWordsInternal(Math.floor(n / 100)); str += 'Hundred '; n %= 100; }
        if (n > 19) { str += b[Math.floor(n / 10)] + ' ' + a[n % 10]; } else { str += a[n]; }
        return str; 
    };
    num = parseFloat(num || 0).toFixed(2);
    if (num === '0.00') return 'USD ZERO DOLLARS ONLY';
    const [dollars, cents] = num.split('.'); // Fixed: num.split('.') instead of split('.')
    let dollarWords = inWordsInternal(parseInt(dollars)).trim(); 
    let centWords = inWordsInternal(parseInt(cents)).trim(); 
    let result = 'USD ';
    if (dollarWords) { result += dollarWords + ' Dollars'; }
    if (centWords && parseInt(cents) > 0) { if (dollarWords) { result += ' and '; } result += centWords + ' Cents'; }
    result += ' Only'; 
    return result.replace(/\s+/g, ' ').toUpperCase(); 
}

const calculateItemTotalUsd = (item, exchangeRate) => {
    if (item.rateCurrency === 'BDT') {
        const rate = parseFloat(item.rate || 0);
        const weight = parseFloat(item.weight || 0);
        const totalBdtForItem = rate * weight;
        const exRate = parseFloat(exchangeRate || 100) || 1;
        return totalBdtForItem / exRate;
    }
    const rate = parseFloat(item.rate || 0);
    const fsc = parseFloat(item.fsc || 0);
    const demandTotal = parseFloat(item.demandCharge || 0);
    return (rate + fsc + demandTotal);
};

const ViewInvoice = () => {
    const { invoiceId } = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSharing, setIsSharing] = useState(false);

    useEffect(() => {
        const fetchInvoice = async () => {
            setLoading(true);
            try {
                const docRef = doc(db, "invoices", invoiceId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setInvoice(docSnap.data());
                } else {
                    console.log("No such document!");
                }
            } catch (error) {
                console.error("Error fetching invoice: ", error);
            }
            setLoading(false);
        };
        if (invoiceId) fetchInvoice();
    }, [invoiceId]);

    const handleShare = async () => {
        if (!invoice) return;
        setIsSharing(true);

        const element = document.getElementById('invoice-content-area');
        const fileName = `Invoice_${invoice.invoiceNo || 'Draft'}.pdf`;

        const opt = {
            margin:       0,
            filename:     fileName,
            image:        { type: 'jpeg', quality: 0.98 },
            html2canvas:  { scale: 2, useCORS: true, scrollY: 0 },
            jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak:    { mode: ['avoid-all', 'css', 'legacy'] }
        };

        try {
            const pdfBlob = await html2pdf().set(opt).from(element).output('blob');
            const file = new File([pdfBlob], fileName, { type: "application/pdf" });

            if (navigator.share) {
                await navigator.share({
                    files: [file],
                    title: 'Invoice Share',
                    text: `Here is the invoice #${invoice.invoiceNo}`,
                });
            } else {
                alert("Sharing not supported on this device. Downloading instead.");
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(file);
                link.download = fileName;
                link.click();
            }

        } catch (error) {
            console.error("Sharing Error:", error);
            if (error.name !== 'AbortError') {
                alert("শেয়ার করার সময় সমস্যা হয়েছে।");
            }
        } finally {
            setIsSharing(false);
        }
    };

    const handleDownloadExcel = () => {
        if (!invoice) return;

        const styles = {
            header: { font: { name: "Times New Roman", sz: 20, bold: true }, alignment: { horizontal: "center", vertical: "center" } },
            address: { font: { name: "Calibri", sz: 11 }, alignment: { horizontal: "center", vertical: "center" } },
            label: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "left", vertical: "center" } },
            value: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "left", vertical: "center" } },
            invoiceLabel: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "left", vertical: "center" } },
            invoiceValue: { font: { name: "Times New Roman", sz: 11, bold: true, color: { rgb: "FF0000" } }, alignment: { horizontal: "left", vertical: "center" } },
            dateValue: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "left", vertical: "center" } },
            billTitle: { font: { name: "Times New Roman", sz: 14, bold: true, underline: true }, alignment: { horizontal: "center" } },
            tableHeader: { font: { name: "Calibri", sz: 11, bold: true }, alignment: { horizontal: "center", vertical: "center" }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }, fill: { fgColor: { rgb: "F2F2F2" } } },
            cellCenter: { font: { name: "Calibri", sz: 11 }, alignment: { horizontal: "center", vertical: "center" }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }, numFmt: '@' },
            cellCurrencyUSD: { font: { name: "Calibri", sz: 11 }, alignment: { horizontal: "right", vertical: "center" }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }, numFmt: '"$ "#,##0.00' },
            cellCurrencyRateBDT: { font: { name: "Calibri", sz: 11 }, alignment: { horizontal: "right", vertical: "center" }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }, numFmt: '"৳ "#,##0.00' },
            cellWeight: { font: { name: "Calibri", sz: 11 }, alignment: { horizontal: "center", vertical: "center" }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }, numFmt: '0.0' },
            cellCurrencyBDT: { font: { name: "Calibri", sz: 11, bold: true }, alignment: { horizontal: "right", vertical: "center" }, border: { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } }, numFmt: '#,##0.00' },
            summaryLabel: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "right" } },
            summaryValueUSD: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "right" }, numFmt: '"$ "#,##0.00' },
            summaryValueBDT: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "right" }, numFmt: '#,##0.00' },
            finalTotal: { font: { name: "Times New Roman", sz: 12, bold: true }, alignment: { horizontal: "right" }, border: { top: { style: "medium" }, bottom: { style: "double" } }, numFmt: '#,##0.00' },
            inWordLabel: { font: { name: "Times New Roman", sz: 11, bold: true }, alignment: { horizontal: "left" } },
            inWordValue: { font: { name: "Times New Roman", sz: 11, italic: true }, alignment: { horizontal: "left", wrapText: true } }
        };

        const rows = [];
        rows.push([{ v: "TWS AIR INTERNATIONAL", s: styles.header }, "", "", "", "", "", "", "", "", "", ""]);
        rows.push([{ v: "10/3/7 Toyenbee Circular Road, Motijheel C/A, Dhaka-1000, BANGLADESH.", s: styles.address }, "", "", "", "", "", "", "", "", "", ""]);
        rows.push([]); 
        const dateStr = new Date(invoice.invoiceDate).toLocaleDateString('en-GB');
        const rLabelIdx = 7;
        const rValueIdx = 8;
        let row1 = new Array(11).fill("");
        row1[0] = { v: "COMPANY NAME", s: styles.label };
        row1[1] = { v: ": " + invoice.companyName, s: styles.value };
        row1[rLabelIdx] = { v: "DATE", s: styles.invoiceLabel };
        row1[rValueIdx] = { v: ": " + dateStr, s: styles.dateValue };
        rows.push(row1);
        let row2 = new Array(11).fill("");
        row2[0] = { v: "CUSTOMER NAME", s: styles.label };
        row2[1] = { v: ": " + invoice.customerName, s: styles.value };
        rows.push(row2);
        let row3 = new Array(11).fill("");
        row3[0] = { v: "ADDRESS", s: styles.label };
        row3[1] = { v: ": " + invoice.customerAddress, s: styles.value };
        row3[rLabelIdx] = { v: "INVOICE NO", s: styles.invoiceLabel };
        row3[rValueIdx] = { v: ": " + invoice.invoiceNo, s: styles.invoiceValue };
        rows.push(row3);
        let row4 = new Array(11).fill("");
        row4[0] = { v: "PHONE", s: styles.label };
        row4[1] = { v: ": " + invoice.customerPhone, s: styles.value };
        rows.push(row4);
        rows.push([]);
        rows.push([{ v: `COURIER BILL - ${formatMonthYear(invoice.invoiceYearMonth)}`, s: styles.billTitle }, "", "", "", "", "", "", "", "", "", ""]);
        rows.push([]);
        let colHeaders = ["DATE", "COUNTRY", "AWB NO."];
        if (invoice.showBuyerNameInView) colHeaders.push("BUYER");
        colHeaders.push("SHIPMENT", "SERVICE", "WEIGHT");
        if (invoice.showUsdInView) {
            if (invoice.showDemandInView) colHeaders.push("RATE", "DEMAND ($)");
            else colHeaders.push("RATE");
            colHeaders.push("FSC ($)", "TOTAL ($)");
        }
        if (invoice.showBdtInView) colHeaders.push("TOTAL (BDT)");
        const headerRow = colHeaders.map(h => ({ v: h, s: styles.tableHeader }));
        rows.push(headerRow);
        
        invoice.items.forEach(item => {
            const row = [];
            row.push({ v: item.date, s: styles.cellCenter });
            row.push({ v: item.country, s: styles.cellCenter });
            row.push({ v: item.awbNo, s: styles.cellCenter });
            if (invoice.showBuyerNameInView) row.push({ v: item.buyerName || '-', s: styles.cellCenter });
            row.push({ v: item.shipment, s: styles.cellCenter });
            row.push({ v: item.service, s: styles.cellCenter });
            row.push({ v: parseFloat(item.weight || 0), s: styles.cellWeight });
            
            const rate = parseFloat(item.rate || 0);
            const fsc = parseFloat(item.fsc || 0);
            const demandTotal = parseFloat(item.demandCharge || 0);
            const isBdtRate = item.rateCurrency === 'BDT';
            
            const totalUsdForItem = calculateItemTotalUsd(item, invoice.exchangeRate);
            const rateStyle = isBdtRate ? styles.cellCurrencyRateBDT : styles.cellCurrencyUSD;

            if (invoice.showUsdInView) {
                if (invoice.showDemandInView) {
                    row.push({ v: safeNum(rate), s: rateStyle });
                    row.push({ v: safeNum(demandTotal), s: styles.cellCurrencyUSD });
                } else {
                    row.push({ v: safeNum(rate + demandTotal), s: rateStyle });
                }
                row.push({ v: safeNum(fsc), s: styles.cellCurrencyUSD });
                row.push({ v: safeNum(totalUsdForItem), s: styles.cellCurrencyUSD });
            }
            if (invoice.showBdtInView) {
                row.push({ v: safeNum(totalUsdForItem * (invoice.exchangeRate || 0)), s: styles.cellCurrencyBDT });
            }
            rows.push(row);
        });
        rows.push([]);
        const totalColIndex = headerRow.length - 1;
        const labelColIndex = totalColIndex - 1;
        const addSummaryRow = (label, value, isFinal = false, isDiscount = false, isUsd = false) => {
            const row = new Array(headerRow.length).fill("");
            let valStyle = isUsd ? styles.summaryValueUSD : styles.summaryValueBDT;
            if (isFinal) valStyle = styles.finalTotal;
            if (isDiscount) valStyle = { ...valStyle, font: { ...valStyle.font, color: { rgb: "FF0000" } } };
            row[labelColIndex] = { v: label, s: styles.summaryLabel };
            row[totalColIndex] = { v: safeNum(value), s: valStyle };
            rows.push(row);
        };
        if (invoice.showUsdInView) {
            addSummaryRow("TOTAL USD:", invoice.totalUsd, false, false, true);
        }
        if (invoice.showBdtInView) {
            addSummaryRow("TOTAL BDT:", invoice.totalBdt);
            addSummaryRow("PREVIOUS DUE:", invoice.previousDue);
            if (invoice.discountAmount > 0) {
                addSummaryRow("DISCOUNT:", invoice.discountAmount, false, true);
            }
            if (invoice.paidEntries && invoice.paidEntries.length > 0) {
                invoice.paidEntries.forEach(entry => {
                    addSummaryRow(`PAID (${entry.date}):`, entry.amount);
                });
            }
            addSummaryRow("FINAL TOTAL:", invoice.finalTotalBdt, true);
        }
        rows.push([]);
        const inWordText = invoice.showBdtInView ? numberToWordsBDT(invoice.finalTotalBdt) : numberToWordsUSD(invoice.totalUsd);
        rows.push([{ v: "IN WORD:", s: styles.inWordLabel }, { v: inWordText, s: styles.inWordValue }]);
        rows.push([]);
        rows.push([]);
        const signedBy = invoice.signedBy || 'MD. MORSHED';
        const signatureDetails = { 'MD. MORSHED': { name: 'MD. MORSHED', phone: '+880 1750-021581', email: null }, 'ANIS KHAN': { name: 'Anis Khan', phone: '+880 1791-479940', email: 'aronnoanis4u@gmail.com' } };
        const currentSigner = signatureDetails[signedBy] || signatureDetails['MD. MORSHED'];
        rows.push([{ v: "Best Regards,", s: styles.label }]);
        rows.push([{ v: currentSigner.name, s: { font: { bold: true, sz: 12 } } }]);
        rows.push([{ v: `Phone: ${currentSigner.phone}`, s: styles.value }]);
        if(currentSigner.email) {
            rows.push([{ v: `Email: ${currentSigner.email}`, s: styles.value }]);
        }
        rows.push([]); 
        if (invoice.showPaymentFooter !== false) {
            rows.push([{ v: "ACCOUNT INFORMATION:", s: { font: { bold: true, underline: true } } }]);
            const addAccRow = (label, val) => {
                let r = new Array(11).fill("");
                r[0] = { v: label, s: styles.label };
                r[1] = { v: ": " + val, s: styles.value };
                rows.push(r);
            };
            addAccRow("Bank Name", "BRAC BANK");
            addAccRow("Account Name", "ARONNO ANIS KHAN");
            addAccRow("Account Number", "1071909180001");
            addAccRow("Branch", "DEMRA SME BRANCH");
            addAccRow("Routing Number", "060271495");
            addAccRow("BKash/Nagad", "+88015702-72101");
        }
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(rows);
        if(!worksheet['!merges']) worksheet['!merges'] = [];
        worksheet['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: headerRow.length - 1 } });
        worksheet['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: headerRow.length - 1 } });
        [3, 4, 5, 6].forEach(rIdx => { worksheet['!merges'].push({ s: { r: rIdx, c: 1 }, e: { r: rIdx, c: 5 } }); });
        [3, 5].forEach(rIdx => { worksheet['!merges'].push({ s: { r: rIdx, c: 8 }, e: { r: rIdx, c: headerRow.length - 1 } }); });
        const billTitleRowIndex = rows.findIndex(r => r[0] && r[0].v && r[0].v.toString().startsWith("COURIER BILL"));
        if(billTitleRowIndex !== -1) { worksheet['!merges'].push({ s: { r: billTitleRowIndex, c: 0 }, e: { r: billTitleRowIndex, c: headerRow.length - 1 } }); }
        const inWordRowIndex = rows.findIndex(r => r[0] && r[0].v === "IN WORD:");
        if (inWordRowIndex !== -1) { worksheet['!merges'].push({ s: { r: inWordRowIndex, c: 1 }, e: { r: inWordRowIndex, c: headerRow.length - 1 } }); }
        const startPayIdx = rows.findIndex(r => r[0] && r[0].v === "Bank Name");
        if(startPayIdx !== -1) {
            for(let i=0; i<6; i++) { worksheet['!merges'].push({ s: { r: startPayIdx+i, c: 1 }, e: { r: startPayIdx+i, c: 4 } }); }
        }
        const MAX_COL_WIDTH = 30;
        const columnWidths = [];
        const tableStartIdx = rows.findIndex(r => r[0] && r[0].v === "DATE");
        if (tableStartIdx !== -1) {
            for (let r = tableStartIdx; r < rows.length; r++) {
                const row = rows[r];
                row.forEach((cell, i) => {
                    let cellValue = "";
                    if (typeof cell === 'object' && cell !== null && cell.v !== undefined) { cellValue = cell.v.toString(); } 
                    else if (cell !== undefined && cell !== null) { cellValue = cell.toString(); }
                    const cellLength = cellValue.length;
                    if (!columnWidths[i] || cellLength > columnWidths[i]) { columnWidths[i] = cellLength; }
                });
            }
        }
        worksheet['!cols'] = columnWidths.map((w, i) => {
            if (i === 0) return { wch: 16 };
            const finalW = Math.min(w + 2, MAX_COL_WIDTH);
            return { wch: Math.max(finalW, 10) };
        });
        XLSX.utils.book_append_sheet(workbook, worksheet, "Invoice");
        let fileName = "Invoice.xlsx";
        if (invoice.invoiceYearMonth) {
            const dateObj = new Date(invoice.invoiceYearMonth + "-01");
            const monthName = dateObj.toLocaleString('default', { month: 'long' }).toUpperCase();
            const yearShort = dateObj.getFullYear().toString().slice(-2);
            const safeCompanyName = (invoice.companyName || "Company").replace(/[^a-z0-9]/gi, '_').toUpperCase();
            fileName = `${safeCompanyName} ${monthName}-${yearShort}.xlsx`;
        }
        XLSX.writeFile(workbook, fileName);
    };

    if (loading) return <p>Loading invoice...</p>;
    if (!invoice) return <p>Invoice not found.</p>;
    
    const totalUsd = invoice.totalUsd || 0;
    const totalBdt = invoice.totalBdt || 0;
    const previousDue = invoice.previousDue || 0;
    const discount = invoice.discountAmount || 0;
    const finalTotalBdt = invoice.finalTotalBdt || 0;

    const showUsd = invoice.showUsdInView !== undefined ? invoice.showUsdInView : true;
    const showDemand = invoice.showDemandInView !== undefined ? invoice.showDemandInView : true;
    const showBdt = invoice.showBdtInView !== undefined ? invoice.showBdtInView : true;
    
    const showBuyerName = invoice.showBuyerNameInView || false;

    const signedBy = invoice.signedBy || 'MD. MORSHED';
    const showPaymentFooter = invoice.showPaymentFooter !== undefined ? invoice.showPaymentFooter : true;
    const invoiceFooterTextOption = invoice.invoiceFooterText || 'Final'; // Get footer text option

    const signatureDetails = {
        'MD. MORSHED': {
            name: 'MD. MORSHED',
            phone: '+880 1750-021581',
            email: null
        },
        'ANIS KHAN': {
            name: 'Anis Khan',
            phone: '+880 1791-479940',
            email: 'aronnoanis4u@gmail.com'
        }
    };

    const currentSigner = signatureDetails[signedBy] || signatureDetails['MD. MORSHED'];

    return (
        <div className="view-invoice-container">
            <div className="invoice-actions no-print">
                <button 
                    onClick={() => navigate(-1)} 
                    style={{backgroundColor: '#6c757d', marginRight: '10px', display: 'flex', alignItems: 'center', gap: '5px'}}
                >
                    <FaArrowLeft /> Back
                </button>
                
                <button 
                    onClick={handleShare} 
                    disabled={isSharing}
                    style={{backgroundColor: '#e67e22', marginRight: '10px', display: 'inline-flex', alignItems: 'center', gap: '5px'}}
                >
                    <FaShareAlt /> {isSharing ? 'Processing...' : 'Share Invoice'}
                </button>

                <button 
                    onClick={() => window.print()}
                    style={{display: 'flex', alignItems: 'center', gap: '5px'}}
                >
                    <FaPrint /> Print Invoice
                </button>
                
                <button 
                    onClick={handleDownloadExcel} 
                    style={{backgroundColor: '#217346', display: 'flex', alignItems: 'center', gap: '5px'}}
                >
                    <FaFileExcel /> Download Excel
                </button>
            </div>

            <div 
                id="invoice-content-area" 
                className="invoice-paper" 
                style={{
                    position: 'relative',
                    minHeight: 'auto',
                    height: 'auto',
                    boxShadow: 'none',
                    margin: '0 auto',
                    overflow: 'visible'
                }}
            >
                <div className="invoice-header">
                    <h1>TWS AIR INTERNATIONAL</h1>
                    <p>10/3/7 Toyenbee Circular Road, Motijheel C/A, Dhaka-1000, BANGLADESH.</p>
                </div>
                
                <div className="customer-details">
                    <div>
                        <p><strong>COMPANY NAME: {invoice.companyName}</strong></p>
                        <p><strong>CUSTOMER NAME: {invoice.customerName}</strong></p>
                        <p><strong>ADDRESS: {invoice.customerAddress}</strong></p>
                        <p><strong>PHONE: {invoice.customerPhone}</strong></p>
                    </div>
                    <div>
                        <p><strong>INVOICE NO:</strong> <span style={{color: 'red', fontWeight: 'bold'}}>{invoice.invoiceNo || 'N/A'}</span></p>
                        <p><strong>DATE:</strong> {new Date(invoice.invoiceDate).toLocaleDateString('en-GB')}</p>
                    </div>
                </div>

                <h3 className="bill-title">COURIER BILL - {formatMonthYear(invoice.invoiceYearMonth)}</h3>

                <table className="invoice-items-table">
                    <thead>
                        <tr>
                            <th>DATE</th>
                            <th>COUNTRY</th>
                            <th>AWB NO.</th>
                            {showBuyerName && <th>BUYER</th>}
                            <th>SHIPMENT</th>
                            <th>SERVICE</th>
                            <th>WEIGHT</th>
                            {showUsd && (
                                <>
                                    <th>RATE</th>
                                    {showDemand && <th>DEMAND ($)</th>}
                                    <th>FSC ($)</th>
                                    <th>TOTAL ($)</th>
                                </>
                            )}
                            {showBdt && <th>TOTAL (BDT)</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {invoice.items && invoice.items.map((item, index) => {
                            const rate = parseFloat(item.rate || 0);
                            const fsc = parseFloat(item.fsc || 0);
                            const demandTotal = parseFloat(item.demandCharge || 0); 
                            const isBdtRate = item.rateCurrency === 'BDT';

                            const totalUsdForItem = calculateItemTotalUsd(item, invoice.exchangeRate);

                            return (
                                <tr key={index}>
                                    <td>{item.date}</td>
                                    <td>{item.country}</td>
                                    <td>{item.awbNo}</td>
                                    {showBuyerName && <td>{item.buyerName || '-'}</td>}
                                    <td>{item.shipment}</td>
                                    <td>{item.service}</td>
                                    <td style={{whiteSpace: 'nowrap'}}>{formatWeight(item.weight)}</td>
                                    
                                    {showUsd && (
                                        <>
                                            {showDemand ? (
                                                <td>{isBdtRate ? '৳' : '$'} {formatCurrency(rate)}</td>
                                            ) : (
                                                <td>{isBdtRate ? '৳' : '$'} {formatCurrency(rate + demandTotal)}</td> 
                                            )}
                                            {showDemand && <td>$ {formatCurrency(demandTotal)}</td>}
                                            <td>$ {formatCurrency(fsc)}</td>
                                            <td>$ {formatCurrency(totalUsdForItem)}</td>
                                        </>
                                    )}
                                    
                                    {showBdt && (
                                        <td>{formatCurrency(totalUsdForItem * invoice.exchangeRate)}</td>
                                    )}
                                </tr>
                            )
                        })}
                    </tbody>
                </table>

                <div className="invoice-summary-view">
                    <div className="summary-left">
                        <div className="in-word-container">
                            <strong>IN WORD: </strong>
                            <span>
                                {
                                    showBdt 
                                    ? numberToWordsBDT(finalTotalBdt)  
                                    : numberToWordsUSD(totalUsd)      
                                }
                            </span>
                        </div>
                    </div>
                    <div className="summary-right">
                        
                        {showUsd && (
                            <p><span>TOTAL IN USD</span> <span>$ {formatCurrency(totalUsd)}</span></p>
                        )}
                        
                        {showBdt ? (
                            <>
                                <p><span>TOTAL IN BDT</span> <span>{formatCurrency(totalBdt)}</span></p>
                                <p><span>PREVIOUS DUE</span> <span>{formatCurrency(previousDue)}</span></p>
                                
                                {discount > 0 && (
                                    <p><span>DISCOUNT</span> <span>- {formatCurrency(discount)}</span></p>
                                )}

                                {invoice.paidEntries && invoice.paidEntries.map((entry, index) => (
                                    <p key={index}><span>{entry.date} PAID</span> <span>- {formatCurrency(entry.amount)}</span></p>
                                ))}
                                
                                <p className="final-total-view">
                                    <span>TOTAL IN BDT</span> <span>{formatCurrency(finalTotalBdt)}</span>
                                </p>
                            </>
                        ) : (
                            <p className="final-total-view">
                                <span>TOTAL IN USD</span> <span>$ {formatCurrency(totalUsd)}</span>
                            </p>
                        )}
                    </div>
                </div>

                <div className="invoice-footer" style={{ marginTop: '50px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div style={{ marginBottom: '20px' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Best Regards,</p>
                        <p style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{currentSigner.name}</p>
                        <p>{currentSigner.phone}</p>
                        {currentSigner.email && <p>Email: {currentSigner.email}</p>}
                    </div>

                    {showPaymentFooter && (
                        <div style={{ marginTop: '20px', fontSize: '12.5px', color: '#333' }}>
                            <p style={{ fontWeight: 'bold', textDecoration: 'underline', marginBottom: '8px' }}>Account Information:</p>
                            <div style={{ display: 'grid', gridTemplateColumns: '120px 10px 1fr', rowGap: '4px' }}>
                                <span style={{ textAlign: 'left' }}>Bank Name</span> <span>:</span> <strong>BRAC BANK</strong>
                                <span style={{ textAlign: 'left' }}>Account Name</span> <span>:</span> <strong>ARONNO ANIS KHAN</strong>
                                <span style={{ textAlign: 'left' }}>Account Number</span> <span>:</span> <strong>1071909180001</strong>
                                <span style={{ textAlign: 'left' }}>Branch</span> <span>:</span> <strong>DEMRA SME BRANCH</strong>
                                <span style={{ textAlign: 'left' }}>Routing Number</span> <span>:</span> <strong>060271495</strong>
                                <span style={{ textAlign: 'left' }}>BKash/Nagad</span> <span>:</span> <strong>+88015702-72101</strong>
                            </div>
                        </div>
                    )}
                </div>

                {/* --- FOOTER TEXT CONDITIONAL RENDERING --- */}
                {invoiceFooterTextOption === 'Demo' ? (
                    <div style={{
                        textAlign: 'center', 
                        marginTop: '40px', 
                        fontWeight: 'bold', 
                        fontSize: '13px', 
                        color: '#e74c3c', 
                        border: '1px dashed #e74c3c', 
                        padding: '10px', 
                        borderRadius: '5px'
                    }}>
                        This is a demo invoice for estimation purposes only. The charges shown are approximate and provided to give an idea of the bill. The final invoice with actual charges will be issued later.
                    </div>
                ) : invoiceFooterTextOption === 'Final' ? (
                    <div style={{
                        textAlign: 'center', 
                        marginTop: '40px', 
                        fontSize: '12px', 
                        color: '#555', 
                        fontStyle: 'italic'
                    }}>
                        This is a system-generated invoice and does not require a signature. If you find any discrepancy, please notify us within 15 days of receiving this invoice. Otherwise, the invoice shall be deemed accepted and considered final.
                    </div>
                ) : (
                    <div style={{
                        textAlign: 'center', 
                        marginTop: '40px', 
                        fontWeight: 'bold', 
                        fontSize: '14px', 
                        color: '#555'
                    }}>
                        Thank you for your business!
                    </div>
                )}

            </div>
        </div>
    );
};

export default ViewInvoice;